/**
 * HTSatelliteMap — high-performance edition
 *
 *  Rebuilt to eliminate per-drag React re-renders of the polyline layer.
 *  How it stays fast:
 *   • Polylines are added ONCE (in a useEffect keyed on `lines`) and cached
 *     in a ref-map. During drag, we only mutate line STYLES (colour, blink
 *     className) via `polyline.setStyle()` — no React reconciliation.
 *   • The distance ring + label chip are direct Leaflet layers we keep in
 *     refs and mutate imperatively on every drag frame — no state churn.
 *   • Drag events are throttled with `requestAnimationFrame` so we never do
 *     more than one recompute per frame even on a fast mouse.
 *   • The React-managed "Live Verdict" chip is a tiny DOM node; the only
 *     state update per frame is the pin coordinate + verdict level.
 *
 *  Basemap: Esri World Imagery satellite (no API key, commercial-use-ok).
 *  Feature layer switch: user can flip between Satellite / Streets /
 *  Infrastructure (Esri "World Topo") from a small overlay control.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// Geo helpers (self-contained; no external deps)
// ============================================================================
const toRad = (d) => (d * Math.PI) / 180;

const pointToSegmentMeters = (lat, lng, lat1, lng1, lat2, lng2) => {
  const mLat = 111_320;
  const mLng = 111_320 * Math.cos(toRad(lat));
  const ax = (lng1 - lng) * mLng, ay = (lat1 - lat) * mLat;
  const bx = (lng2 - lng) * mLng, by = (lat2 - lat) * mLat;
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(ax, ay);
  let t = -(ax * dx + ay * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = ax + t * dx, qy = ay + t * dy;
  return Math.hypot(qx, qy);
};

const nearestDistanceOnLine = (lat, lng, geometry = []) => {
  let best = Infinity;
  for (let i = 1; i < geometry.length; i++) {
    const [la1, lo1] = geometry[i - 1];
    const [la2, lo2] = geometry[i];
    const d = pointToSegmentMeters(lat, lng, la1, lo1, la2, lo2);
    if (d < best) best = d;
  }
  return best === Infinity ? null : best;
};

// Great-circle distance in metres — used to detect when the user has strayed
// far enough from the last-fetched pin to warrant re-hitting Overpass.
const haversineM = (lat1, lng1, lat2, lng2) => {
  const R = 6_371_000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const CRIT_M = 100;
const WARN_M = 500;
const colorForDist = (d) => (d <= CRIT_M ? '#e11d48' : d <= WARN_M ? '#d97706' : '#0284c7');

const resolveVerdict = (closestM) => {
  if (closestM == null) return { level: 'unknown', label: 'No HT lines detected in 2 km', color: '#059669', bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-900' };
  if (closestM <= CRIT_M)  return { level: 'critical', label: `HT line passes over your plot · ${Math.round(closestM)} m`, color: '#e11d48', bg: 'bg-rose-50', border: 'border-rose-400', text: 'text-rose-900' };
  if (closestM <= WARN_M)  return { level: 'warning', label: `HT line within safety corridor · ${Math.round(closestM)} m`, color: '#d97706', bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-900' };
  return { level: 'clear', label: `Nearest HT line ${closestM < 1000 ? `${Math.round(closestM)} m` : `${(closestM / 1000).toFixed(2)} km`} away · Safe`, color: '#0284c7', bg: 'bg-sky-50', border: 'border-sky-300', text: 'text-sky-900' };
};

// Custom pin (radar ring pulses when `scanning` is true)
const makePinIcon = (scanning) => L.divIcon({
  className: 'am-ht-pin',
  html:
    `<div style="position:relative;width:22px;height:22px;">` +
    `<div style="width:22px;height:22px;background:#065f46;border:3px solid #facc15;border-radius:50%;box-shadow:0 4px 10px rgba(0,0,0,.5);position:relative;z-index:2;"></div>` +
    (scanning
      ? `<div class="am-radar-ring"    style="position:absolute;inset:-8px;border-radius:50%;border:2px solid #facc15;opacity:.9;"></div>` +
        `<div class="am-radar-ring am-radar-delay" style="position:absolute;inset:-8px;border-radius:50%;border:2px solid #facc15;opacity:.7;"></div>`
      : ''
    ) +
    `<div style="position:absolute;top:26px;left:50%;transform:translateX(-50%);background:#0f172a;color:#facc15;font-size:9px;font-weight:900;padding:1px 6px;border-radius:8px;white-space:nowrap;letter-spacing:.06em;z-index:3;">` +
    `${scanning ? 'SCANNING…' : 'TAP OR DRAG'}</div>` +
    `</div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// ============================================================================
// Tile-layer switcher — Satellite / Streets / Infrastructure
// ============================================================================
const BASEMAPS = {
  satellite: {
    label: 'Satellite',
    urls: [
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
    ],
    attribution: 'Tiles © Esri — Maxar, Earthstar Geographics',
  },
  streets: {
    label: 'Streets',
    urls: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
    attribution: '© OpenStreetMap contributors',
  },
  infra: {
    // Esri World Topo highlights power lines, substations, roads and terrain —
    // way more useful than plain streets for HT-clearance decisions.
    label: 'Infrastructure',
    urls: [
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
    ],
    attribution: 'Tiles © Esri — Sources: Esri, USGS, NOAA',
  },
};

// ============================================================================
// The component
// ============================================================================
const HTSatelliteMap = ({
  centerLat,
  centerLng,
  lines = [],
  substations = [],
  towers = [],
  waterbodies = [],
  radiusM = 2000,
  onPinChange,
  selectedId,
  onLineClick,
  height = 480,
}) => {
  const mapEl        = useRef(null);
  const mapRef       = useRef(null);
  const markerRef    = useRef(null);
  const polyRef      = useRef(new Map());       // id -> { core, halo, blink }
  const overlayRef   = useRef([]);              // water polygons + substations + towers
  const ringRef      = useRef(null);            // L.Circle
  const searchRingRef= useRef(null);            // 2 km search-radius circle — follows the pin
  const chipRef      = useRef(null);            // L.Marker (divIcon)
  const scanTimeout  = useRef(null);
  const rafRef       = useRef(null);
  const basemapRef   = useRef({ layers: [], key: 'satellite' });
  const closestRef   = useRef(null);            // { line?, distance_m } for imperative reads

  const [pin, setPin]          = useState({ lat: centerLat, lng: centerLng });
  const [scanning, setScanning]= useState(false);
  const [basemap,  setBasemap] = useState('satellite');
  const [verdict,  setVerdict] = useState(() => resolveVerdict(null));
  // Auto-rescan state — fetches fresh Overpass geometry once the pin has
  // strayed more than RESCAN_TRIGGER_M from the last-fetched centre.
  const RESCAN_TRIGGER_M = 500;
  const [refetching, setRefetching] = useState(false);
  const [override,   setOverride]   = useState(null); // { lines, substations, towers, waterbodies }
  const lastFetchRef = useRef({ lat: centerLat, lng: centerLng });
  const rescanAbortRef = useRef(null);

  // Which sets does the map actually render? The initial props are the
  // "authoritative" scan for the property centre; once the user drags far
  // enough and we've fetched a fresh set, those take precedence.
  const effLines        = override?.lines        ?? lines;
  const effSubstations  = override?.substations  ?? substations;
  const effTowers       = override?.towers       ?? towers;
  const effWaterbodies  = override?.waterbodies  ?? waterbodies;

  // Kick off an ad-hoc Overpass fetch for (lat, lng). Debounced by cancelling
  // any prior in-flight request.
  const rescanAt = async (lat, lng) => {
    if (rescanAbortRef.current) rescanAbortRef.current.abort?.();
    const ctrl = new AbortController();
    rescanAbortRef.current = ctrl;
    setRefetching(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/scan/ht-and-water`, {
        params: { lat, lng }, timeout: 55000, signal: ctrl.signal,
      });
      const ht = res.data?.ht || {};
      const wb = res.data?.waterbody || {};
      setOverride({
        lines:        ht.lines        || [],
        substations:  ht.substations  || [],
        towers:       ht.towers       || [],
        waterbodies:  (wb.features    || []).map((f) => ({ ...f })),
      });
      lastFetchRef.current = { lat, lng };
    } catch (err) {
      if (!axios.isCancel?.(err) && err?.name !== 'CanceledError') {
        // Silent — the current polyline set stays visible; a chip will indicate the try failed.
      }
    } finally {
      // Only clear if we're the latest fetch
      if (rescanAbortRef.current === ctrl) {
        rescanAbortRef.current = null;
        setRefetching(false);
      }
    }
  };

  // Cheap distance calc against a lat/lng (uses cached `lines` prop OR the
  // freshly-fetched override set if the user has strayed far enough).
  const computeClosest = (lat, lng) => {
    let best = null;
    const src = override?.lines ?? lines;
    for (const ln of (src || [])) {
      const d = nearestDistanceOnLine(lat, lng, ln.geometry || []);
      if (d == null) continue;
      if (!best || d < best.distance_m) best = { ...ln, distance_m: d };
    }
    return best;
  };

  // ------------------- Map init (once) --------------------------------------
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
      preferCanvas: true,        // faster polyline hit-testing vs SVG
    }).setView([centerLat, centerLng], 16);

    // Initial basemap
    const bm = BASEMAPS.satellite;
    basemapRef.current.layers = bm.urls.map((u, i) =>
      L.tileLayer(u, { maxZoom: 19, opacity: i === 0 ? 1 : 0.85, attribution: i === 0 ? bm.attribution : undefined }).addTo(map),
    );
    basemapRef.current.key = 'satellite';

    // 2 km search circle — kept in a ref so it FOLLOWS the pin. Was
    // previously fire-and-forget at (centerLat, centerLng), which made the
    // dashed yellow radius stay glued to the admin's uploaded Google
    // location even after the user dropped the pin somewhere else.
    searchRingRef.current = L.circle([centerLat, centerLng], {
      radius: radiusM, color: '#facc15', weight: 1.5, dashArray: '5 5',
      fillColor: '#facc15', fillOpacity: 0.05,
    }).addTo(map);

    // Ring layer (created empty; positioned in updateOverlays)
    ringRef.current = L.circle([centerLat, centerLng], {
      radius: 0.01, color: '#0284c7', weight: 2, opacity: 0, dashArray: '3 6', fill: false,
    }).addTo(map);

    // Chip marker (created empty)
    chipRef.current = L.marker([centerLat, centerLng], {
      icon: L.divIcon({ className: 'am-ht-chip', html: '', iconSize: [1, 1] }),
      interactive: false,
      opacity: 0,
    }).addTo(map);

    // Draggable pin
    const marker = L.marker([centerLat, centerLng], { draggable: true, icon: makePinIcon(false) }).addTo(map);
    markerRef.current = marker;

    // Google-Maps-style tap-to-drop-pin: click anywhere on the map to move
    // the pin to that location, then run the same rescan flow as a drag-end.
    // Always triggers a fresh Overpass fetch so the verdict reflects the
    // dropped location instead of the stale scan from the property centre.
    const dropPinAt = (lat, lng) => {
      marker.setLatLng([lat, lng]);
      setScanning(true);
      marker.setIcon(makePinIcon(true));
      // Optimistic UI update using whatever geometry we already have.
      updateOverlays(lat, lng);
      setPin({ lat, lng });
      restylePolylines(lat, lng);
      // Always rescan on tap — the whole point of dropping the pin is to see
      // HT / water bodies around THIS spot, not the property centre. Keep the
      // "SCANNING…" pin/radar visible until Overpass returns.
      rescanAt(lat, lng).finally(() => {
        setScanning(false);
        marker.setIcon(makePinIcon(false));
      });
      if (onPinChange) onPinChange(lat, lng);
    };
    map.on('click', (e) => {
      // Skip if the click originated on a leaflet control / marker
      if (e.originalEvent?.target?.closest?.('.leaflet-marker-icon, .leaflet-control, .am-ht-chip')) return;
      dropPinAt(e.latlng.lat, e.latlng.lng);
    });

    // rAF-throttled drag handler — mutates ring/chip directly, no React re-render
    // except a single tiny setState for the verdict banner.
    const onDrag = (e) => {
      const { lat, lng } = e.target.getLatLng();
      if (rafRef.current) return;               // already scheduled
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        updateOverlays(lat, lng);
        setPin({ lat, lng });                   // one lightweight React update
      });
    };
    marker.on('dragstart', () => {
      setScanning(true);
      marker.setIcon(makePinIcon(true));
    });
    marker.on('drag', onDrag);
    marker.on('dragend', (e) => {
      const { lat, lng } = e.target.getLatLng();
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      updateOverlays(lat, lng);
      setPin({ lat, lng });
      // Rebuild polyline styles (colour + blink) based on the settled pin.
      restylePolylines(lat, lng);
      // Auto re-fetch Overpass when we've strayed >RESCAN_TRIGGER_M from
      // the last fetched centre — brings in HT lines / water bodies that
      // sit outside the original 2 km sample. Keep the scanning radar on
      // until rescan lands so the user doesn't see a premature "no lines" chip.
      const last = lastFetchRef.current;
      const strayedM = haversineM(lat, lng, last.lat, last.lng);
      if (strayedM > RESCAN_TRIGGER_M) {
        rescanAt(lat, lng).finally(() => {
          setScanning(false);
          marker.setIcon(makePinIcon(false));
        });
      } else {
        if (scanTimeout.current) clearTimeout(scanTimeout.current);
        scanTimeout.current = setTimeout(() => {
          setScanning(false);
          marker.setIcon(makePinIcon(false));
        }, 380);
      }
      if (onPinChange) onPinChange(lat, lng);
    });

    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 250);
    // Initial paint of overlays for the default pin
    updateOverlays(centerLat, centerLng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------- Basemap switch ---------------------------------------
  useEffect(() => {
    if (!mapRef.current || basemapRef.current.key === basemap) return;
    // Remove previous tile layers, add new ones. Polylines survive untouched.
    for (const l of basemapRef.current.layers) mapRef.current.removeLayer(l);
    const bm = BASEMAPS[basemap];
    basemapRef.current.layers = bm.urls.map((u, i) =>
      L.tileLayer(u, { maxZoom: 19, opacity: i === 0 ? 1 : 0.85, attribution: i === 0 ? bm.attribution : undefined }).addTo(mapRef.current),
    );
    basemapRef.current.key = basemap;
    // Keep polylines above tiles
    polyRef.current.forEach(({ halo, blink, core }) => {
      halo?.bringToFront(); blink?.bringToFront(); core?.bringToFront();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap]);

  // ------------------- Rebuild polylines when `lines` changes ---------------
  useEffect(() => {
    if (!mapRef.current) return;
    // Remove any previous polylines
    polyRef.current.forEach(({ core, halo, blink }) => {
      if (core)  mapRef.current.removeLayer(core);
      if (halo)  mapRef.current.removeLayer(halo);
      if (blink) mapRef.current.removeLayer(blink);
    });
    polyRef.current.clear();

    (effLines || []).forEach((ln) => {
      const geo = ln.geometry || [];
      if (geo.length < 2) return;
      const dist = ln.distance_m ?? Infinity;
      const color = colorForDist(dist);
      const halo = L.polyline(geo, { color, weight: 8, opacity: 0.3, lineCap: 'round' }).addTo(mapRef.current);
      const blink = L.polyline(geo, { color: '#ff002d', weight: 14, opacity: dist <= CRIT_M ? 0.55 : 0, lineCap: 'round', className: dist <= CRIT_M ? 'am-ht-blink' : '' }).addTo(mapRef.current);
      const core = L.polyline(geo, { color, weight: 3.5, opacity: 0.95, lineCap: 'round', className: dist <= CRIT_M ? 'am-ht-core-danger' : '' }).addTo(mapRef.current);
      core.bindTooltip(
        `<b>${ln.voltage_label || 'HT line'}</b><br/>${dist.toFixed(0)} m from pin${dist <= CRIT_M ? '<br/><b style="color:#ff002d">⚠ over your land</b>' : ''}`,
        { sticky: true, direction: 'top' },
      );
      if (onLineClick) core.on('click', () => onLineClick(ln));
      polyRef.current.set(ln.id, { core, halo, blink, geo, meta: ln });
    });
    // Recompute verdict + polyline styles against the CURRENT pin so freshly
    // fetched geometry (post-rescan) is reflected immediately in the banner.
    if (markerRef.current) {
      const { lat, lng } = markerRef.current.getLatLng();
      updateOverlays(lat, lng);
      restylePolylines(lat, lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effLines]);

  // ------------------- Water bodies + substations + towers overlay ---------
  useEffect(() => {
    if (!mapRef.current) return;
    // Clear previous overlays
    for (const l of overlayRef.current) mapRef.current.removeLayer(l);
    overlayRef.current = [];

    // Water bodies — blue polygons (or polylines for rivers/streams).
    (effWaterbodies || []).forEach((wb) => {
      const geo = wb.geometry || [];
      if (geo.length < 1) return;
      const isRiver = /river|stream|canal|drain/i.test(wb.type || '');
      if (wb.is_area && geo.length >= 3) {
        const poly = L.polygon(geo, {
          color: '#0ea5e9', weight: 1.5, opacity: 0.85,
          fillColor: '#38bdf8', fillOpacity: 0.35,
          className: 'am-water-shape',
        }).addTo(mapRef.current);
        poly.bindTooltip(`💧 ${wb.name ? wb.name + ' · ' : ''}${wb.type} · ${Math.round(wb.distance_m)} m`, { sticky: true, direction: 'top' });
        overlayRef.current.push(poly);
      } else if (geo.length >= 2) {
        // River / stream — thick blue polyline
        const line = L.polyline(geo, {
          color: '#0284c7', weight: isRiver ? 5 : 3, opacity: 0.85,
          lineCap: 'round',
          className: 'am-water-shape',
        }).addTo(mapRef.current);
        line.bindTooltip(`💧 ${wb.name ? wb.name + ' · ' : ''}${wb.type} · ${Math.round(wb.distance_m)} m`, { sticky: true, direction: 'top' });
        overlayRef.current.push(line);
      } else if (geo.length === 1) {
        // Node (small pond) — draw a small circle
        const [la, ln] = geo[0];
        const c = L.circle([la, ln], { radius: 12, color: '#0284c7', fillColor: '#38bdf8', fillOpacity: 0.45, weight: 1.5 })
          .addTo(mapRef.current);
        c.bindTooltip(`💧 ${wb.name ? wb.name + ' · ' : ''}${wb.type} · ${Math.round(wb.distance_m)} m`, { sticky: true, direction: 'top' });
        overlayRef.current.push(c);
      }
    });

    // Substations — orange filled squares (rendered as circles for simplicity)
    (effSubstations || []).forEach((s) => {
      const geo = s.geometry || [];
      if (geo.length >= 3) {
        const poly = L.polygon(geo, {
          color: '#f97316', weight: 2, opacity: 1,
          fillColor: '#fdba74', fillOpacity: 0.35,
        }).addTo(mapRef.current);
        poly.bindTooltip(`⚡ Substation${s.voltage_label ? ' · ' + s.voltage_label : ''}${s.name ? ' · ' + s.name : ''} · ${Math.round(s.distance_m)} m`, { sticky: true, direction: 'top' });
        overlayRef.current.push(poly);
      } else if (geo.length >= 1) {
        const [la, ln] = geo[0];
        const m = L.marker([la, ln], {
          icon: L.divIcon({
            className: 'am-substation-icon',
            html: '<div style="width:16px;height:16px;background:#f97316;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);transform:rotate(45deg);"></div>',
            iconSize: [16, 16], iconAnchor: [8, 8],
          }),
        }).addTo(mapRef.current);
        m.bindTooltip(`⚡ Substation${s.voltage_label ? ' · ' + s.voltage_label : ''} · ${Math.round(s.distance_m)} m`, { sticky: true, direction: 'top' });
        overlayRef.current.push(m);
      }
    });

    // HT Towers — small red triangles (imply a line runs through)
    (effTowers || []).forEach((t) => {
      const m = L.marker([t.lat, t.lng], {
        icon: L.divIcon({
          className: 'am-tower-icon',
          html: '<div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-bottom:11px solid #dc2626;filter:drop-shadow(0 1px 2px rgba(0,0,0,.5));"></div>',
          iconSize: [12, 11], iconAnchor: [6, 11],
        }),
      }).addTo(mapRef.current);
      m.bindTooltip(`🗼 HT Tower · ${Math.round(t.distance_m)} m`, { sticky: true, direction: 'top' });
      overlayRef.current.push(m);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effWaterbodies, effSubstations, effTowers]);

  // ------------------- Imperative helpers (mutation, not React) -------------
  const updateOverlays = (lat, lng) => {
    // Move the yellow dashed 2 km search circle so it stays centred on the
    // pin the user actually dropped (was previously stuck at the property's
    // uploaded Google coordinates).
    if (searchRingRef.current) {
      searchRingRef.current.setLatLng([lat, lng]);
    }
    const closest = computeClosest(lat, lng);
    // Ring
    if (ringRef.current) {
      if (closest && closest.distance_m <= radiusM) {
        ringRef.current.setLatLng([lat, lng]);
        ringRef.current.setRadius(closest.distance_m);
        ringRef.current.setStyle({ color: colorForDist(closest.distance_m), opacity: 0.9 });
      } else {
        ringRef.current.setStyle({ opacity: 0 });
      }
    }
    // Chip
    if (chipRef.current) {
      if (closest && closest.distance_m <= radiusM) {
        const dist = closest.distance_m;
        const label = dist < 1000 ? `${Math.round(dist)} m away` : `${(dist / 1000).toFixed(2)} km away`;
        const html = `<div style="background:rgba(15,23,42,0.9);color:#fff;font-size:10px;font-weight:800;padding:3px 8px;border-radius:9999px;white-space:nowrap;border:1.5px solid ${colorForDist(dist)};box-shadow:0 4px 8px rgba(0,0,0,.35);transform:translate(6px,-18px);">${closest.voltage_label || 'HT'} · ${label}</div>`;
        chipRef.current.setIcon(L.divIcon({ className: 'am-ht-chip', html, iconSize: [1, 1] }));
        chipRef.current.setLatLng([lat, lng]);
        chipRef.current.setOpacity(1);
      } else {
        chipRef.current.setOpacity(0);
      }
    }
    // Verdict (cheap React update — one small div re-render)
    setVerdict(resolveVerdict(closest?.distance_m ?? null));
  };

  // Re-colour and re-toggle blink classes on the existing polylines. Called on
  // dragend so the danger-red flash matches the final position; cheap because
  // it uses `setStyle`, no layer add/remove.
  const restylePolylines = (lat, lng) => {
    polyRef.current.forEach(({ core, halo, blink, geo }) => {
      const dist = nearestDistanceOnLine(lat, lng, geo) ?? Infinity;
      const color = colorForDist(dist);
      halo.setStyle({ color });
      core.setStyle({ color });
      blink.setStyle({ opacity: dist <= CRIT_M ? 0.55 : 0 });
      // toggle blink className via the internal SVG/Canvas path node
      const pathEl = core._path || (core._renderer && core._renderer._container);
      if (pathEl && pathEl.classList) {
        pathEl.classList.toggle('am-ht-core-danger', dist <= CRIT_M);
      }
      const blinkEl = blink._path || (blink._renderer && blink._renderer._container);
      if (blinkEl && blinkEl.classList) {
        blinkEl.classList.toggle('am-ht-blink', dist <= CRIT_M);
      }
    });
  };

  // ------------------- Cleanup ---------------------------------------------
  useEffect(() => () => {
    if (scanTimeout.current) clearTimeout(scanTimeout.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
  }, []);

  // Reset pin
  const resetPin = () => {
    if (markerRef.current) markerRef.current.setLatLng([centerLat, centerLng]);
    if (mapRef.current) mapRef.current.setView([centerLat, centerLng], 16);
    // Drop any auto-fetched override so we snap back to the property's
    // authoritative scan.
    setOverride(null);
    lastFetchRef.current = { lat: centerLat, lng: centerLng };
    updateOverlays(centerLat, centerLng);
    restylePolylines(centerLat, centerLng);
    setPin({ lat: centerLat, lng: centerLng });
    if (onPinChange) onPinChange(centerLat, centerLng);
  };

  return (
    <div className="relative w-full" data-testid="ht-satellite-map">
      <style>{`
        @keyframes am-ht-blink-kf { 0%,100% { opacity: 0.15 } 50% { opacity: 0.75 } }
        .am-ht-blink { animation: am-ht-blink-kf 900ms ease-in-out infinite; }
        @keyframes am-ht-core-danger-kf { 0%,100% { stroke: #e11d48 } 50% { stroke: #ff002d } }
        .am-ht-core-danger { animation: am-ht-core-danger-kf 900ms ease-in-out infinite; }
        @keyframes am-radar-kf { 0% { transform: scale(1); opacity: 0.9 } 100% { transform: scale(2.6); opacity: 0 } }
        .am-radar-ring { animation: am-radar-kf 1.2s ease-out infinite; }
        .am-radar-ring.am-radar-delay { animation-delay: 0.6s; }
        @keyframes am-verdict-pulse { 0%,100% { transform: scale(1) } 50% { transform: scale(1.02) } }
        .am-verdict-live { animation: am-verdict-pulse 1s ease-in-out infinite; }
      `}</style>

      <div ref={mapEl} style={{ width: '100%', height, borderRadius: 12, overflow: 'hidden' }} role="region" aria-label="Satellite map showing HT power lines around your plot" />

      {/* Basemap switcher — top center on desktop, bottom-left on mobile so it doesn't overlap the verdict/coord chips */}
      <div className="absolute bottom-2 left-2 sm:top-2 sm:left-1/2 sm:bottom-auto sm:-translate-x-1/2 z-[500] bg-white/95 backdrop-blur rounded-full shadow-lg border border-slate-200 p-1 flex gap-1" data-testid="ht-basemap-switcher">
        {Object.entries(BASEMAPS).map(([k, v]) => (
          <button
            key={k}
            type="button"
            onClick={() => setBasemap(k)}
            className={`text-[9px] sm:text-[10px] font-black uppercase tracking-widest px-2 sm:px-2.5 py-1 rounded-full transition-all ${basemap === k ? 'bg-emerald-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid={`ht-basemap-${k}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Coord + reset (top-left) — compact on mobile */}
      <div className="absolute top-2 left-2 z-[500] bg-white/95 backdrop-blur rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 shadow-lg border border-slate-200 max-w-[150px] sm:max-w-[240px]">
        <div className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
          Your plot pin
          {scanning && (
            <span className="inline-flex items-center gap-1 text-[8px] font-black uppercase tracking-widest bg-amber-500 text-white rounded-full px-1.5 py-0.5 ml-auto">
              <span className="w-1 h-1 rounded-full bg-white animate-ping" /> Scanning
            </span>
          )}
        </div>
        <div className="text-[10px] sm:text-[11px] font-mono text-slate-800 mt-0.5 truncate">
          {pin.lat.toFixed(4)}, {pin.lng.toFixed(4)}
        </div>
        {refetching && (
          <div
            className="mt-1 inline-flex items-center gap-1 text-[8px] sm:text-[9px] font-black uppercase tracking-widest bg-sky-100 border border-sky-300 text-sky-800 rounded-full px-1.5 py-0.5"
            data-testid="ht-refetching"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sky-600 animate-ping" />
            Fetching…
          </div>
        )}
        {override && !refetching && (
          <div
            className="mt-1 inline-flex items-center gap-1 text-[8px] sm:text-[9px] font-black uppercase tracking-widest bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-full px-1.5 py-0.5"
            data-testid="ht-override-active"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
            Live pin
          </div>
        )}
        <div className="hidden sm:block text-[9px] text-slate-500 mt-1 leading-tight">
          Tap or drag the pin. Stray {'>'}500 m → auto-refresh from OSM.
        </div>
        <button
          type="button"
          onClick={resetPin}
          className="mt-1 text-[9px] sm:text-[10px] font-bold text-emerald-700 hover:text-emerald-900 underline"
          data-testid="ht-satellite-reset"
        >
          Reset
        </button>
      </div>

      {/* Live Verdict banner (top-right) — with total lines-detected count on mobile */}
      <div
        className={`absolute top-2 right-2 z-[500] rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 shadow-lg border-2 ${refetching ? 'bg-sky-50 border-sky-400 text-sky-900 am-verdict-live' : `${verdict.bg} ${verdict.border} ${verdict.text} ${scanning ? 'am-verdict-live' : ''}`} max-w-[52vw] sm:max-w-[260px]`}
        data-testid="ht-satellite-verdict"
        data-verdict={refetching ? 'scanning' : verdict.level}
      >
        <div className="flex items-center justify-between gap-1.5">
          <div className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest opacity-70">
            {refetching ? 'Scanning' : 'Live verdict'}
          </div>
          {!refetching && (
            <span
              className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest bg-slate-900/85 text-white rounded-full px-1.5 py-0.5"
              data-testid="ht-lines-count-badge"
              title="HT lines detected in this scan"
            >
              {(effLines?.length || 0)} line{(effLines?.length || 0) === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {refetching ? (
            <>
              <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full shrink-0 bg-sky-500 animate-ping" />
              <div className="text-[10px] sm:text-[11px] font-black leading-tight">
                Fetching HT + water bodies around this pin…
              </div>
            </>
          ) : (
            <>
              <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full shrink-0" style={{ background: verdict.color, boxShadow: `0 0 8px ${verdict.color}` }} />
              <div className="text-[10px] sm:text-[11px] font-black leading-tight">{verdict.label}</div>
            </>
          )}
        </div>
        {!refetching && verdict.level === 'critical' && (
          <div className="text-[8px] sm:text-[9px] font-bold uppercase tracking-widest text-rose-700 mt-1 animate-pulse">
            ⚡ Line pulsing on the map
          </div>
        )}
      </div>

      {/* Legend (bottom-right) — hidden on mobile to keep the map readable */}
      <div className="hidden sm:block absolute bottom-2 right-2 z-[500] bg-white/95 backdrop-blur rounded-xl px-3 py-2 shadow-lg border border-slate-200">
        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Legend</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] font-bold">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-600 animate-pulse" /> HT ≤100m</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-600" /> HT ≤500m</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-sky-600" /> HT &gt;500m</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-sky-400" /> Water body</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-orange-500 rotate-45" /> Substation</span>
          <span className="inline-flex items-center gap-1"><span className="w-0 h-0 border-l-[5px] border-r-[5px] border-b-[8px] border-l-transparent border-r-transparent border-b-rose-600" /> Tower</span>
        </div>
      </div>
    </div>
  );
};

export default HTSatelliteMap;
